// src/modules/Onboarding/runtime/types.ts
//
// Single type-only contract for the Onboarding runtime. Vue-free (only
// `ComputedRef` via `import type`) — safe for external/* to import without
// breaking the manifest generator.
//
// Consumed by:
//   • external/{variables,actions,featureFlags}.ts — `satisfies` + `ctx` typing
//   • useOnboardingBuilderState.ts — ctx assembly, plugin proxy casts
//   • useJetApi.ts — `JetApi` return type

import type { ComputedRef } from 'vue'

// ── Vue plugin handles ───────────────────────────────────────────────────────
// `$analytic` and `$absmartly` are Vue 2 plugins still accessed via the
// instance proxy (`getCurrentInstance().proxy.$xxx`).

export interface AnalyticPlugin {
	logEvent: (name: string, properties?: Record<string, unknown>) => void
	// Returns the underlying plugin promise (see src/plugins/Amplitude.js) — the
	// landing-welcome host op chains `.then` to fire ScreenView/OnboardingStart
	// only after the user properties are set (legacy LandingView parity).
	setUserProperties: (props: Record<string, unknown>) => Promise<unknown>
}

export interface ABSmartlyPlugin {
	treatment: (key: string) => number
	peek: (key: string) => number
	attributes: (attrs: Record<string, unknown>) => void
}

// ── Jet onboarding analytics ────────────────────────────────────────────────
// Mirrors `useOnboardingStepEvents()` plus the canonical constants and helper
// maps assembled by `useJetApi`.

interface JetStepEvents {
	currentStepId: string | null
	currentTitle: string | null
	previousStepId: string | null
	previousTitle: string | null
	isBackwards: boolean
	lastAnswerData: unknown
}

export interface JetApi {
	stepEvents: JetStepEvents
	jetStepShow: (screen: unknown, isBackwards?: boolean, isVariation?: boolean) => void
	jetStepDone: (isBackwards: boolean) => void
	jetPopupShow: (modal: unknown) => void
	jetPopupDone: (modal: unknown, closeType: number) => void
	jetButtonClick: (buttonName: string) => void
	jetHandleDelegatedClick: (event: Event) => void
	jetSnapshotDelegatedClick: (event: Event) => void
	jetAlertDone: (alertDescription: string, data: unknown) => void
	jetScreenSpecificEvent: (eventName: string, properties: Record<string, unknown>) => void
	jetCaptureAnswer: (data: { name: string; properties: Record<string, unknown> }) => void
	BUTTON_NAME: {
		PRIVACY_POLICY: string
		TERMS_OF_USE: string
		LANGUAGE_SELECTOR: string
		SYSTEM_OF_MEASUREMENT: string
	}
	JET_SCREEN_EVENTS: Set<string>
	LEGACY_TO_JET_EVENT: Record<string, { jetName: string; valueKey: string }>
	CUSTOM_EVENT_NAME: {
		HEIGHT_FORM_ERROR: string
	}
	SCREEN_DONE_TO_JET: Record<string, string>
	closeReasonToType: (reason?: string) => number
}

// ── Host operations surface ──────────────────────────────────────────────────
// Complex actions (onDataChanged, onEventTriggered, email-form ops) delegate to
// the stateful composable through this narrow interface. Implementation lives
// in useOnboardingBuilderState.ts (and useEmailFormHost for the email-form ops);
// external/actions only calls via ctx.host.*.

interface HostOperations {
	processDataChange: (data: Record<string, unknown>) => void
	processEvent: (data: { name: string; properties: Record<string, unknown>; isUserProperty?: boolean }) => void
	// Landing welcome/loader lifecycle — the legacy LandingView step-0 analytics, driven
	// from the screen JSON's `ds:visit` / `ds:leave`. See useOnboardingBuilderState §6.7.
	processLandingWelcomeVisit: () => void
	processLandingWelcomeDone: () => void
	// Email-form host ops — see useEmailFormHost.ts for behaviour.
	setEmail: (value: string) => void
	submitEmail: () => void
	submitGdprYes: () => void
	submitGdprNo: () => void
	closeGdprModal: () => void
	// Name-form host op — owned inline in useOnboardingBuilderState.ts; reads
	// surveyModelRef.name (synced via OB data-flow), runs the survey/main save
	// pipeline, then navigates next.
	submitName: () => Promise<void> | void
	// Bare next-screen navigation — used by stateless screens (e.g.
	// personalized-plan-activity) whose only CTA action is to advance.
	navigateNext: () => void
	// Height/weight measurement-system writes — mirror the legacy
	// `UnitSystem.vue` `updateStoreValue`: persist `measurement_system_{height,weight}`
	// plus the legacy unified `measurement_system` (['metric'] | ['imperial']).
	setHeightUnit: (unit: string) => void
	setWeightUnit: (unit: string) => void
	// Records the canonical measure value as the matching required analytic
	// user-property (Height/Weight/TargetWeight) on the measure screen's leave —
	// the renderer's generic `ds:leave` payload is the INCOMING screen, so the
	// per-screen `*ScreenDone` mis-attributes the value; this is wired directly
	// to each measure screen so the property is set from the store reliably.
	setMeasureUserProperty: (measure: 'height' | 'weight' | 'targetWeight') => void
	// Health-data-processing consent toggle on the height screen — writes the
	// `health_data_processing_consent` string into the survey `consents` array.
	setHealthConsent: (checked: boolean) => void
	// Clears the blur-gated error for a measure field (height/weight/targetWeight)
	// when the user resumes typing — mirrors the legacy `onUpdate → showError=false`.
	clearMeasureError: (variable: string) => void
	// Blur-time error analytics for the measure screens — mirror the legacy
	// `onFormErrorAnalytics`/`onBlur` (Amplitude + Jet logical event + alert).
	trackHeightError: () => void
	trackWeightError: () => void
	trackTargetWeightError: () => void
	// Opens the any-medical health-alert modal on the footer CTA tap when a
	// blocking condition is selected, otherwise advances. Modal visibility is
	// driven by this tap (transient flag), not reactively by the answer.
	submitMedicalConditions: () => void
	// Closes the health-alert modal (Edit-my-answer / backdrop / re-entry reset).
	closeMedicalModal: () => void
	// Pregnancy screen: open a soft safety-warning modal on a relevant answer
	// (pregnant / breastfeeding / prefer-not-to-answer), else advance. Mirrors the
	// medical flag but reads the `pregnancy` variable.
	submitPregnancy: () => void
	// Closes the pregnancy safety modal (backdrop / re-entry reset).
	closePregnancyModal: () => void
	// Resets result-screen loader-sequence flags on screen enter so a re-visit
	// restarts the chain instead of firing every loader at once.
	resetResultState: () => void
	// Closes the result-screen analyze modal (flips `analyzeModalOpen` false) and
	// latches the next loader to resume. Variable-driven, like the medical modal.
	closeAnalyzeModal: () => void
}

// ── Store shapes (trimmed to what external/* actually reads) ─────────────────

interface ExternalMainStore {
	user: { email?: string } | null
	email: string | null
	hasPrevSurvey: boolean
	getCountryCode: string
	setLanguageModalState: (payload: { showModal: boolean; screenName: string }) => void
}

// Note: useEmailFormHost reads `isAuthenticated`, `getUserEmail`, and calls
// `saveEmail()` directly off `useMainStore()` rather than through ctx —
// keeping the ExternalMainStore surface narrow. If any external/* entry needs
// these in the future, lift them here.

interface ExternalSurveyStore {
	getPropertyValue: (key: string, isData?: boolean) => unknown
}

interface ExternalUserFlagsStore {
	getIsOrganicTrafficOnboarding: boolean
	// Raw flag maps as returned by the store's `getFlags` getter (`{ remote, local }`,
	// trimmed to `local` — the only side external/* reads). Values stay `unknown`:
	// besides booleans the local map holds e.g. `funnelFeatureFlag` (string|null),
	// so consumers must coerce (`Boolean(...)`) per flag.
	getFlags: { local: Record<string, unknown> }
}

interface ExternalLocationStore {
	isCrmAutologin: boolean
	getQueryParam: (param: string) => string | undefined
}

interface ExternalI18nStore {
	/** Active funnel locale, e.g. `en`, `de-DE`. */
	getLanguage: string
}

// ── Measurement screens derived state ────────────────────────────────────────
// Single computed snapshot shared by the height / weight / target-weight screen
// bindings. Owned by `useOnboardingBuilderState.ts` (heavy logic — BMI, ranges,
// country-based unit defaults), surfaced to the manifest variables 1:1 through
// `ctx.computed.measurements`. The `*Valid` / `*ShowError` flags reproduce the
// legacy Vuelidate rules (`HeightPageControls` 91–214 cm, `WeightPageControls`
// 35.83–226.8 kg + BMI ≥ 19, `TargetWeightPageControls` min/max + BMI ≥ 18.5).

export interface MeasurementsState {
	// Canonical survey values (cm / kg) — fed straight to InputMeasure `value`.
	height: number | null
	weight: number | null
	targetWeight: number | null
	// Selected display unit + offered segments (UK adds `st`).
	heightUnit: string
	weightUnit: string
	weightUnitOptions: string[]
	// Health-data consent (height screen) read off the survey `consents` array.
	healthDataConsent: boolean
	// BMI summary (wellness-review): numeric value for BmiScale + the category
	// string ('underweight' | 'normal' | 'overweight' | 'obesity') the screen's
	// `op:in` cards gate on. Null/'' until both height and weight are known.
	bmiValue: number | null
	bmiCategory: string
	// Wellness-review stat rows (legacy BodyTypeBlock, WALKING_FEMALE branch):
	//   activityLevelCode — from `activity_level` (easy→beginner, medium→intermediate,
	//                       hard→advanced); defaults to 'beginner'.
	//   lifestyleCode     — from `typical_day` (all_day_moves→active, else sedentary);
	//                       '' when unanswered so the row stays hidden.
	activityLevelCode: string
	lifestyleCode: string
	// Body-image key for the wellness-review summary. Maps the `physical_build`
	// answer (slim/medium/plump/owerweight) onto the four body images
	// (thin/skinny_fat/medium/overweight), defaulting to 'thin' — mirrors the
	// legacy BodyTypeBlock `bodyTypeMap[answer] ?? thin`. Without this, an answer
	// like 'slim' matched no image and nothing rendered.
	bodyImageKey: string
	// Validity + CTA gating.
	heightValid: boolean
	heightFormValid: boolean
	weightValid: boolean
	targetWeightValid: boolean
	// Inline input-border error (per field). Blur-gated — only true after the
	// field is left invalid, never while typing (legacy InputHeight/InputWeight
	// `:error` was driven by blur, not keystrokes).
	heightShowError: boolean
	weightShowError: boolean
	targetWeightShowError: boolean
	consentShowError: boolean
	// Distinct min/max error cards (legacy MeasurementsError min vs max copy).
	// Exactly one of each pair is ever true; both false while valid or typing.
	weightErrorMin: boolean
	weightErrorMax: boolean
	targetWeightErrorMin: boolean
	targetWeightErrorMax: boolean
	// True when the goal weight is valid AND not a gain (target ≤ current) — gates
	// the green "Realistic goal!" success card so it hides for weight-gain goals.
	targetWeightRealisticGoal: boolean
	// Internal — target-weight bounds reused by trackTargetWeightError; not
	// exposed as manifest variables.
	minTargetWeight: number
	maxTargetWeight: number
}

// ── ExternalCtx — single argument passed to every variable/action/flag ───────

export interface ExternalCtx {
	mainStore: ExternalMainStore
	surveyStore: ExternalSurveyStore
	userFlagsStore: ExternalUserFlagsStore
	locationStore: ExternalLocationStore
	i18nStore: ExternalI18nStore
	$analytic: AnalyticPlugin
	jet: JetApi
	computed: {
		weightLossBlockers: ComputedRef<string>
		followingStatementAvg: ComputedRef<number>
		// Email-form computed values — owned by useEmailFormHost.
		email: ComputedRef<string>
		emailError: ComputedRef<boolean>
		saving: ComputedRef<boolean>
		isButtonDisabled: ComputedRef<boolean>
		showGdprModal: ComputedRef<boolean>
		// Name-form saving flag — owned inline in useOnboardingBuilderState.ts.
		nameSaving: ComputedRef<boolean>
		// Height/weight/target-weight screen derived state — see MeasurementsState.
		measurements: ComputedRef<MeasurementsState>
		// True while the any-medical health-alert modal is open (transient flag).
		medicalModalOpen: ComputedRef<boolean>
		// True while the pregnancy-screen safety modal is open (transient flag).
		pregnancyModalOpen: ComputedRef<boolean>
	}
	host: HostOperations
}

// ── Type tags shipped in runtime-manifest.json ───────────────────────────────
// Screen-condition authors need to know what shape each variable returns so
// they can pick valid operators (=, >, includes, …). We derive the tag from
// the `resolve` return type via `TagFor<T>` and enforce it at compile time
// through the `defineVariable` / `defineFeatureFlag` helpers below. Unions
// degrade to `'unknown'` — intentional: the manifest consumer can't narrow
// them, so the operator set is reduced.

type VariableTypeTag = 'boolean' | 'number' | 'string' | 'boolean[]' | 'number[]' | 'string[]' | 'unknown'

type FeatureFlagTypeTag = 'boolean' | 'number'

// Tag → value-type: the call-site picks the tag literal, then TS infers Tag
// and constrains `resolve` to return the matching shape. Inverted from a
// "T → Tag" direction because generic inference through conditional types on
// the return value is unreliable (`TagFor<T>` collapses to one branch).

type ValueForVariableTag<Tag extends VariableTypeTag> = Tag extends 'boolean'
	? boolean
	: Tag extends 'number'
		? number
		: Tag extends 'string'
			? string
			: Tag extends 'boolean[]'
				? boolean[]
				: Tag extends 'number[]'
					? number[]
					: Tag extends 'string[]'
						? string[]
						: unknown

type ValueForFlagTag<Tag extends FeatureFlagTypeTag> = Tag extends 'boolean'
	? boolean
	: Tag extends 'number'
		? number
		: never

// ── Described spec shapes ────────────────────────────────────────────────────
// Each external entry is `{ description, [type,] resolve }`. `description` is
// a one-line English string shipped in the manifest for the builder UI.

interface DescribedVariable<Tag extends VariableTypeTag = VariableTypeTag> {
	/** English one-liner — what this variable represents. Shipped in runtime-manifest. */
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForVariableTag<Tag>
}

interface DescribedAction<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	F extends (...args: any[]) => unknown = (...args: any[]) => unknown,
> {
	/** English one-liner — what this action does when the renderer invokes it. */
	description: string
	resolve: (ctx: ExternalCtx) => F
}

interface DescribedFeatureFlag<Tag extends FeatureFlagTypeTag = FeatureFlagTypeTag> {
	/** English one-liner — what condition this flag expresses. */
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForFlagTag<Tag>
}

// ── Category contracts (used via `satisfies` in external/*) ──────────────────
// The map-level check only asserts "each entry is a described spec". The
// per-entry `type`-vs-`resolve` link is enforced inside `defineVariable<T>` /
// `defineFeatureFlag<T>` / `defineAction<F>`, where T is inferred from the
// individual `resolve` body. Parametrizing the map with `<any>` would collapse
// `TagFor<any>` to a single tag (TS picks the first conditional branch), so we
// erase the generic here and use a plain shape instead.

interface AnyDescribedVariable {
	description: string
	type: VariableTypeTag
	resolve: (ctx: ExternalCtx) => unknown
}

interface AnyDescribedAction {
	description: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	resolve: (ctx: ExternalCtx) => (...args: any[]) => unknown
}

interface AnyDescribedFeatureFlag {
	description: string
	type: FeatureFlagTypeTag
	resolve: (ctx: ExternalCtx) => boolean | number
}

export type VariablesMap = Record<string, AnyDescribedVariable>
export type ActionsMap = Record<string, AnyDescribedAction>
export type FeatureFlagsMap = Record<string, AnyDescribedFeatureFlag>

// ── `define*` helpers ────────────────────────────────────────────────────────
// Without these, TS can't infer T per-entry from `resolve` and match it against
// the declared `type` tag. With them, each key's T is inferred from the factory
// body and the tag becomes a compile-time constraint:
//
//   hasEmail: defineVariable({
//     description: '…',
//     type: 'string',                    // ❌ Type '"string"' not assignable to '"boolean"'
//     resolve: (ctx) => Boolean(…),
//   })

export function defineVariable<Tag extends VariableTypeTag>(spec: {
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForVariableTag<Tag>
}): DescribedVariable<Tag> {
	return spec
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defineAction<F extends (...args: any[]) => unknown>(spec: {
	description: string
	resolve: (ctx: ExternalCtx) => F
}): DescribedAction<F> {
	return spec
}

export function defineFeatureFlag<Tag extends FeatureFlagTypeTag>(spec: {
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForFlagTag<Tag>
}): DescribedFeatureFlag<Tag> {
	return spec
}
